// hooks/use-web-socket-4/observerFactory.ts
import type { Dispatch, RefObject, SetStateAction } from 'react';
import { ReadyState, type ReadyStateType } from './socket-manager';

export interface Handlers<T, U = T> {
  onOpen?: (event: Event) => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (event: Event) => void;
  onMessage?: (data: U) => void;
  filter?: (data: T) => boolean;
  transform?: (data: T) => U;
}

// Handlers are read through the ref on every event, so callers always get their
// latest callbacks without the observer having to be recreated.
export const createObserver = <T = unknown, U = T>(
  fullUrl: string,
  handlersRef: RefObject<Handlers<T, U>>,
  setReadyState: Dispatch<SetStateAction<{ [url: string]: ReadyStateType }>>
) => ({
  onOpen: (event: Event) => {
    handlersRef.current.onOpen?.(event);
    setReadyState((prev) => ({ ...prev, [fullUrl]: ReadyState.OPEN }));
  },
  onClose: (event: CloseEvent) => {
    handlersRef.current.onClose?.(event);
    setReadyState((prev) => ({ ...prev, [fullUrl]: ReadyState.CLOSED }));
  },
  onError: (event: Event) => {
    handlersRef.current.onError?.(event);
    setReadyState((prev) => ({ ...prev, [fullUrl]: ReadyState.CLOSED }));
  },
  onMessage: (data: T | null) => {
    if (data === null) return;

    const handlers = handlersRef.current;
    if (handlers.filter && !handlers.filter(data)) {
      return;
    }

    let transformedData: U;
    try {
      transformedData = handlers.transform
        ? handlers.transform(data)
        : (data as U);
    } catch (error) {
      console.warn('Error during data transformation:', error);
      return;
    }

    handlers.onMessage?.(transformedData);
  },
});
